'use client';

import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/cn';
import type { Owner } from '@repo/api';
import { Avatar, Rating, Tag } from '@/components/ui';
import { IconChevronRight } from '@repo/svgs';
import { contactUrl } from '@/utils/routes';
import { isTopRated, titleCase } from '@repo/utils';

export interface OwnerListCardProps {
  /** Core owner data from API */
  owner: Owner;
  /** Optional className to customize container */
  className?: string;
  tagVariant?: 'topRated' | 'worstLandlord';
  /**
   * Optional click handler. When given, the card keeps its old behaviour and
   * renders a non-navigating <div> — the caller owns what the click does.
   * When omitted (every call site today) the card renders a real <a href>.
   */
  onClick?: () => void;
  /** Allow analytics data attributes (data-analytics-event/props) to be
   * forwarded to the clickable root so the global click listener can read them. */
  [key: `data-${string}`]: string | undefined;
}

export function OwnerListCard({
  owner,
  className,
  onClick,
  tagVariant = 'topRated',
  ...props
}: OwnerListCardProps) {
  const hasReviews =
    typeof owner.averageRating === 'number' && typeof owner.reviewCount === 'number';
  const isOwnerTopRated = isTopRated(owner.averageRating, owner.reviewCount);
  const buildingCount = typeof owner.numBuildings === 'number' ? owner.numBuildings : null;
  const unitCount = typeof owner.numUnits === 'number' ? owner.numUnits : null;
  const name = owner.name || 'Owner';
  const avatarLabel = name.trim().charAt(0).toUpperCase();

  // Get the primary role (first role or fallback)
  const primaryRole = owner.roles && owner.roles.length > 0 ? owner.roles[0] : 'Property manager';

  const rootClassName = cn(
    'flex gap-4 items-start p-2 md:p-4',
    'bg-primary md:border border-secondary rounded-2xl',
    'cursor-pointer transition-colors duration-200',
    'hover:bg-primary-hover',
    className,
  );

  /*
   * A real <a href>, not a div with router.push.
   *
   * The card navigated fine for a person but was invisible to a crawler: the
   * only thing linking to a contact page from /nyc/top-rated-contacts and
   * /nyc/top-reviewed-contacts was a click handler, so those two pages passed
   * no link equity to the 100 contacts they list.
   *
   * The <div> branch is kept for the two cases with no destination — a caller
   * supplying its own `onClick`, and an owner with no id, which `handleClick`
   * already treated as a no-op. Neither occurs at any call site today, so in
   * practice every card is now an anchor. `...props` (the data-analytics-*
   * attrs the building owners page passes) lands on whichever root renders,
   * so the global click listener is unaffected either way.
   */
  const body = (
    <>
      {/* Avatar */}
      <Avatar type="Owner" label={avatarLabel} size={44} />

      {/* Content Container */}
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        {/* Title Container */}
        <div className="flex items-center gap-4 w-full">
          <h3 className="flex-1 text-body-2-high text-primary truncate">{titleCase(name)}</h3>
          <IconChevronRight className="w-3 h-3 icon-tertiary flex-shrink-0" />
        </div>

        {/* Subtitle Container - Rating and Tag */}
        <div className="flex items-center gap-1">
          <Rating value={owner.averageRating ?? 0} count={owner.reviewCount as number} size="sm" />
          {((hasReviews && isOwnerTopRated) || tagVariant === 'worstLandlord') && (
            <>
              <span className="text-body-2 text-secondary">·</span>
              <Tag variant={tagVariant} className="shadow-none !p-0" linkable={false} />
            </>
          )}
        </div>

        {/* Lower Container - Role, Buildings, Units */}
        <div className="flex items-center gap-1 text-caption text-secondary">
          <span className="truncate">{primaryRole}</span>

          {typeof buildingCount === 'number' && (
            <>
              <span>·</span>
              <span className="truncate">
                {buildingCount} building{buildingCount === 1 ? '' : 's'}
              </span>
            </>
          )}

          {typeof unitCount === 'number' && (
            <>
              <span>·</span>
              <span className="flex-1 truncate">
                {unitCount.toLocaleString()} unit{unitCount === 1 ? '' : 's'}
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );

  if (onClick || !owner.id) {
    return (
      <div className={rootClassName} onClick={onClick} {...props}>
        {body}
      </div>
    );
  }

  return (
    <Link href={contactUrl(owner)} className={rootClassName} {...props}>
      {body}
    </Link>
  );
}
